import { FormSection } from 'components/form';
import { Typography, Link, LinearProgress, Box, Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './Wait.module.scss';
import { useFormState } from 'hooks/useFormState';
import { useFormContext } from 'react-hook-form';
import { Replay } from '@mui/icons-material';
import { watchForDeviceReading } from 'lib/firebase';

export function CreateDeviceModalWaitSection() {
    const { setValue, watch } = useFormContext();
    const initialDeviceId = watch('deviceId');
    const { currentFormSectionIndex, formSections, setFormSectionError } =
        useFormState();
    const [timer, setTimer] = useState(0);
    const [readingSuccess, setReadingSuccess] = useState(false);
    const deviceLastReading = watch('lastReading');

    const formSectionTitle = 'Wait';

    const hasReadingError = !readingSuccess && timer === 100;

    const runTimer = useMemo(() => {
        return (
            formSections.indexOf(formSectionTitle) === currentFormSectionIndex
        );
    }, [currentFormSectionIndex, formSections]);

    // Manage the submit button state
    const { setIsFormSubmitDisabled } = useFormState();
    useEffect(() => {
        if (
            currentFormSectionIndex === formSections.indexOf(formSectionTitle)
        ) {
            if (readingSuccess) {
                setIsFormSubmitDisabled(false);
            } else {
                setIsFormSubmitDisabled(true);
            }
        } else {
            if (readingSuccess || hasReadingError) {
                handleRetry();
            }
        }
    }, [
        setIsFormSubmitDisabled,
        currentFormSectionIndex,
        formSections,
        readingSuccess,
        setReadingSuccess,
        hasReadingError,
    ]);

    const getLastDeviceReading = useCallback(async () => {
        const reading = await watchForDeviceReading(initialDeviceId);
        if (reading) {
            setValue('lastReading', reading);
            setReadingSuccess(true);
            setTimer(100);
        }
    }, [initialDeviceId, setValue]);

    useEffect(() => {
        if (!readingSuccess) {
            if (!runTimer) {
                setTimer(0);
            }

            if (runTimer) {
                const interval = setInterval(
                    () =>
                        setTimer((oldTimer) => {
                            const newTimer = oldTimer + 100 / 600;
                            return newTimer > 100 ? 100 : newTimer;
                        }),
                    100
                );
                getLastDeviceReading();

                return () => clearInterval(interval);
            }
        }
    }, [getLastDeviceReading, readingSuccess, runTimer]);

    const color = readingSuccess
        ? 'success'
        : hasReadingError
        ? 'error'
        : 'primary';

    const waitMessage = readingSuccess
        ? 'Reading successfully retrieved.'
        : hasReadingError
        ? 'There may be an error reading from the monitor.'
        : 'Wait for the monitor to send a reading.';

    const additionalHelp =
        (readingSuccess
            ? 'Please click next to continue the deployment process.'
            : undefined) || hasReadingError;

    const handleRetry = () => {
        setTimer(0);
        setReadingSuccess(false);
    };

    useEffect(() => {
        setFormSectionError({
            section: formSectionTitle,
            field: {
                name: 'lastReading',
                error: Object.keys(deviceLastReading).length === 0,
            },
        });
    }, [deviceLastReading, setFormSectionError]);

    return (
        <FormSection title="Wait">
            <Typography variant="h6" className={styles.header}>
                {waitMessage}
            </Typography>
            <LinearProgress
                variant={'determinate'}
                value={
                    readingSuccess || hasReadingError
                        ? 100
                        : timer > 100
                        ? 100
                        : timer
                }
                color={color}
            />
            {additionalHelp && (
                <Typography className={styles.additionalHelp}>
                    {additionalHelp}
                    {hasReadingError && (
                        <Button
                            variant="contained"
                            onClick={handleRetry}
                            startIcon={<Replay />}
                        >
                            Retry
                        </Button>
                    )}
                </Typography>
            )}
            <Box>
                <Typography>
                    Experienced an error? Click{' '}
                    <Link
                        href="https://support.tankutility.com/hc/en-us/articles/360033213294-Inconsistent-Readings"
                        target="__blank"
                    >
                        here
                    </Link>
                </Typography>
            </Box>
        </FormSection>
    );
}
